import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { transformDbPatientToFrontend } from "@/lib/dataTransformers";
import { Patient } from "@/components/PatientCard";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export const usePatients = (wardName?: string) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["patients", organizationId, currentBranchId, wardName],
    queryFn: async () => {
      let query = supabase.from("patients").select("*")
        .is("physical_discharge_completion_time", null)
        .eq("branch_id", currentBranchId)
        .eq("organization_id", organizationId!);
      if (wardName) query = query.eq("ward_name", wardName);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(transformDbPatientToFrontend);
    },
    enabled: currentBranchId != null && !!organizationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const usePatientsByWardName = (wardName?: string) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["patients", "by-ward-name", organizationId, currentBranchId, wardName],
    queryFn: async () => {
      let query = supabase.from("patients").select("*")
        .is("physical_discharge_completion_time", null)
        .eq("branch_id", currentBranchId)
        .eq("organization_id", organizationId!);
      if (wardName) query = query.eq("ward_name", wardName);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(transformDbPatientToFrontend);
    },
    enabled: !!wardName && currentBranchId != null && !!organizationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const usePatientsByRoom = (room?: string) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["patients", "by-room", organizationId, currentBranchId, room],
    queryFn: async () => {
      let query = supabase.from("patients").select("*")
        .is("physical_discharge_completion_time", null)
        .eq("branch_id", currentBranchId)
        .eq("organization_id", organizationId!);
      if (room) query = query.eq("room", room);
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(transformDbPatientToFrontend);
    },
    enabled: !!room && currentBranchId != null && !!organizationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const usePatientsByDischargeDate = (startDate?: Date, endDate?: Date) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["patients", "by-discharge-date", organizationId, currentBranchId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async () => {
      let query = supabase.from("patients").select("*")
        .eq("branch_id", currentBranchId)
        .eq("organization_id", organizationId!);
      if (startDate && endDate) {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];
        query = query.gte("expected_discharge", startStr).lte("expected_discharge", endStr);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map(transformDbPatientToFrontend);
    },
    enabled: !!startDate && !!endDate && currentBranchId != null && !!organizationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const usePatientsByMDDate = (startDate?: Date, endDate?: Date) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ["patients", "by-md-date", organizationId, currentBranchId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: async () => {
      if (!startDate || !endDate || currentBranchId == null || !organizationId) return [];
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const { data: activities, error: activitiesError } = await supabase
        .from("activities").select("patient_id, completion_time")
        .eq("workflow_step", "MD").eq("status", "completed")
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("completion_time", `${startStr}T00:00:00+05:30`).lte("completion_time", `${endStr}T23:59:59+05:30`);
      if (activitiesError) throw activitiesError;

      const mdActivities = activities || [];
      const mdPatientIds = [...new Set(mdActivities.map(a => a.patient_id))];

      let mdPatients: any[] = [];
      if (mdPatientIds.length > 0) {
        const { data: mdPatientsData, error: mdPatientsError } = await supabase
          .from("patients").select("*")
          .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
          .in("id", mdPatientIds);
        if (mdPatientsError) throw mdPatientsError;
        mdPatients = mdPatientsData || [];
      }

      const mdTimeMap = new Map(mdActivities.map(a => [a.patient_id, a.completion_time]));
      const mdTimelinePatients = mdPatients.map(patient => ({
        ...transformDbPatientToFrontend(patient),
        mdCompletionTime: mdTimeMap.get(patient.id) || null,
      }));

      const { data: expectedPatientsData, error: expectedPatientsError } = await supabase
        .from("patients").select("*")
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .gte("expected_discharge", startStr).lte("expected_discharge", endStr)
        .is("physical_discharge_completion_time", null);
      if (expectedPatientsError) throw expectedPatientsError;

      const expectedPatients = (expectedPatientsData || []).filter(p => !mdTimeMap.has(p.id));
      const expectedTimelinePatients = expectedPatients.map(patient => ({
        ...transformDbPatientToFrontend(patient),
        mdCompletionTime: null,
      }));

      return [...mdTimelinePatients, ...expectedTimelinePatients];
    },
    enabled: !!startDate && !!endDate && currentBranchId != null && !!organizationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
